import { APP_INITIALIZER, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { NoopAnimationsModule } from '@angular/platform-browser/animations';

import { AppRoutingModule } from './app-routing.module';
import { HttpClient, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import { FormsModule } from '@angular/forms';
import uk from '@angular/common/locales/uk';
import en from '@angular/common/locales/en';
import ru from '@angular/common/locales/ru';

import { AppComponent } from './app.component';
import { NZ_I18N } from 'ng-zorro-antd/i18n';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { authInterceptor } from './services/auth.interceptor';
import { ownerAuthInterceptor } from './services/owner-auth.interceptor';
import { marketologAuthInterceptor } from './services/marketolog-auth.interceptor';
import { getInitLanguage } from './services/translate.service';
import { AccountSessionService } from './services/saved-accounts/account-session.service';
import { AccountTabSyncService } from './services/saved-accounts/account-tab-sync.service';

registerLocaleData(uk);
registerLocaleData(en);
registerLocaleData(ru);

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    NoopAnimationsModule,
    FormsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],
  providers: [
    { provide: LOCALE_ID, useValue: getInitLanguage() },
    {
      provide: NZ_I18N,
      useValue: getInitLanguage(),
    },
    provideHttpClient(
      withInterceptorsFromDi(),
      // Order matters: the marketolog interceptor runs first so that on /marketolog/** it wins
      // over a concurrently signed-in owner or cabinet session in the same browser profile.
      withInterceptors([marketologAuthInterceptor, ownerAuthInterceptor, authInterceptor]),
    ),
    // Saved-accounts FR-9a/FR-6: sweep idle sessions, reconcile the active map against the role
    // keys actually present, then touch lastUsedAt — once, at bootstrap.
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [AccountSessionService],
      useFactory: (sessionService: AccountSessionService) => () => sessionService.startup(),
    },
    // Saved-accounts FR-28: a switch / logout / logout-all in another tab full-loads this tab when
    // it sits inside the affected role's cabinet, so it never acts with another account's token.
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [AccountTabSyncService],
      useFactory: (tabSync: AccountTabSyncService) => () => tabSync.start(),
    },
  ],
})
export class AppModule {}
